import { useEffect, useMemo, useRef, useState } from 'react';

import type { OptionValue } from '@/components/organisms/Select/types';
import { fetchAllDefaultFilters } from '@/lib/api/filters';
import { fetchCustomFilters } from '@/lib/api/filters/fetchCustomFilters';
import type { CountryFilterOption } from '@/lib/api/types/filters';
import { hasData, hasResults, isDefinedValue } from '@/lib/guards';
import type { SupportedLanguage } from '@/utils';

import { getAvailableFilterValues, getNewlyUnavailableValues } from './availability';
import { getCustomFilterApiKeys } from './parsers';

import type {
  CustomFilterApiKeySet,
  FilterValueSets,
  NewDefaultFiltersData,
  NewFilters,
  NewFiltersData,
} from './types';

const EMPTY_CUSTOM_FILTER_KEYS: CustomFilterApiKeySet = new Set();

export const EMPTY_FILTER_DATA: NewFiltersData = {
  custom: {},
  default: {},
};

type CountryOptionWithDisabled = CountryFilterOption & { disabled?: boolean };

type UseFilterDataLoaderParams = {
  draftFilterRequest: NewFilters;
  groupId?: string;
  groupName?: string;
  hasSelectedCountry: boolean;
  hydrated: boolean;
  lang?: SupportedLanguage;
  onSingleCountrySelected: (countryId: string) => void;
  onValuesUnavailable: (unavailable: FilterValueSets) => void;
};

function normalizeCountries(value: unknown): CountryFilterOption[] {
  if (Array.isArray(value)) return value as CountryFilterOption[];
  if (hasResults<CountryFilterOption>(value)) return value.results;
  if (hasData<CountryFilterOption>(value)) return value.data;
  return [];
}

/**
 * Keeps already-known countries in the list and marks currently unavailable
 * values disabled, so selected business-unit values do not disappear while the
 * draft narrows the rest of the dropdown options.
 */
function mergeCountryAvailability(
  previousOptions: CountryOptionWithDisabled[],
  availableOptions: CountryFilterOption[]
): CountryOptionWithDisabled[] {
  const availableIds = new Set(availableOptions.map((country) => String(country.id)));
  const byId = new Map<string, CountryOptionWithDisabled>();

  for (const country of previousOptions) {
    byId.set(String(country.id), country);
  }

  for (const country of availableOptions) {
    byId.set(String(country.id), country);
  }

  return Array.from(byId.values())
    .map((country) => ({
      ...country,
      disabled: !availableIds.has(String(country.id)),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Keeps countries checked in the draft enabled even without data, so a country
 * the period made unavailable can still be unchecked; once unchecked it is
 * disabled like any other country without data.
 */
export function enableSelectedCountries(
  defaultFilters: NewDefaultFiltersData,
  selected: readonly OptionValue[]
): NewDefaultFiltersData {
  if (!Array.isArray(defaultFilters.countries) || !selected.length) return defaultFilters;

  const selectedIds = new Set(selected.map(String));
  const countries = (defaultFilters.countries as CountryOptionWithDisabled[]).map((country) =>
    country.disabled && selectedIds.has(String(country.id)) ? { ...country, disabled: false } : country
  );

  return { ...defaultFilters, countries };
}

/** Identifies what a load was made for, keeping the period apart from the filter selection. */
function getLoadKeys(filters: NewFilters) {
  const { period, ...selection } = filters;

  return { periodKey: JSON.stringify(period), selectionKey: JSON.stringify(selection) };
}

function getSingleCountryValue(value: unknown): string | null {
  const countries = normalizeCountries(value);

  if (countries.length !== 1) return null;

  const countryId = countries[0]?.id;

  return isDefinedValue(countryId) ? String(countryId) : null;
}

export function useFilterDataLoader({
  draftFilterRequest,
  groupId,
  groupName,
  hasSelectedCountry,
  hydrated,
  lang,
  onSingleCountrySelected,
  onValuesUnavailable,
}: UseFilterDataLoaderParams) {
  const [filterData, setFilterData] = useState<NewFiltersData>(EMPTY_FILTER_DATA);
  const [loadedCustomFiltersSourceKey, setLoadedCustomFiltersSourceKey] = useState<string | null>(null);
  const countryOptionsRef = useRef<CountryOptionWithDisabled[]>([]);
  const previousLoadRef = useRef<{ available: FilterValueSets; periodKey: string; selectionKey: string } | null>(null);
  const hasSelectedCountryRef = useRef(false);
  const previousSourceKeyRef = useRef<string | null>(null);
  const customFiltersSourceKey = groupId && groupName ? `${groupId}:${groupName}` : null;
  const customFiltersLoaded =
    customFiltersSourceKey !== null && loadedCustomFiltersSourceKey === customFiltersSourceKey;
  const allowedCustomFilterKeys = useMemo(() => getCustomFilterApiKeys(filterData.custom), [filterData.custom]);
  const exposedCustomFilterKeys = customFiltersLoaded ? allowedCustomFilterKeys : EMPTY_CUSTOM_FILTER_KEYS;
  const persistedCustomFilterKeys = customFiltersLoaded ? allowedCustomFilterKeys : undefined;
  const visibleCustomFilters = customFiltersLoaded ? filterData.custom : EMPTY_FILTER_DATA.custom;

  useEffect(() => {
    hasSelectedCountryRef.current = hasSelectedCountry;
  }, [hasSelectedCountry]);

  useEffect(() => {
    if (previousSourceKeyRef.current === customFiltersSourceKey) return;

    countryOptionsRef.current = [];
    previousLoadRef.current = null;
    previousSourceKeyRef.current = customFiltersSourceKey;
  }, [customFiltersSourceKey]);

  useEffect(() => {
    if (!hydrated) return;

    if (!groupId || !groupName || !lang) {
      countryOptionsRef.current = [];
      previousLoadRef.current = null;
      setFilterData(EMPTY_FILTER_DATA);
      setLoadedCustomFiltersSourceKey(null);
      return;
    }

    const selectedGroupId = groupId;
    const selectedGroupName = groupName;
    const selectedLang = lang;
    const selectedCustomFiltersSourceKey = customFiltersSourceKey;

    let isCurrent = true;

    async function loadFilterData() {
      try {
        const [defaultFilters, customFilters] = await Promise.all([
          fetchAllDefaultFilters({
            group: selectedGroupId,
            lang: selectedLang,
            filters: draftFilterRequest,
          }),
          fetchCustomFilters(selectedGroupId, selectedGroupName, draftFilterRequest),
        ]);

        if (!isCurrent) return;

        const availableCountries = normalizeCountries(defaultFilters.countries);
        const countries = mergeCountryAvailability(countryOptionsRef.current, availableCountries);
        const previousLoad = previousLoadRef.current;
        const load = {
          available: getAvailableFilterValues(defaultFilters, customFilters),
          ...getLoadKeys(draftFilterRequest),
        };
        // only a filter change on its own deselects; a period change, even alongside one, keeps the selection
        const isSelectionChange =
          previousLoad !== null &&
          previousLoad.periodKey === load.periodKey &&
          previousLoad.selectionKey !== load.selectionKey;

        countryOptionsRef.current = countries;
        previousLoadRef.current = load;

        setFilterData({
          custom: customFilters,
          default: {
            ...defaultFilters,
            countries,
          },
        });
        setLoadedCustomFiltersSourceKey(selectedCustomFiltersSourceKey);

        if (isSelectionChange) onValuesUnavailable(getNewlyUnavailableValues(previousLoad.available, load.available));

        // only a group with a single country gets it selected; narrowing filters just disable the others
        const onlyCountryValue = countries.length === 1 ? getSingleCountryValue(availableCountries) : null;

        if (onlyCountryValue && !hasSelectedCountryRef.current) {
          onSingleCountrySelected(onlyCountryValue);
        }
      } catch (error) {
        if (!isCurrent) return;

        console.error('NewFiltersProvider failed to fetch filters:', error);
        setFilterData(EMPTY_FILTER_DATA);
        setLoadedCustomFiltersSourceKey(null);
      }
    }

    void loadFilterData();

    return () => {
      isCurrent = false;
    };
  }, [
    customFiltersSourceKey,
    draftFilterRequest,
    groupId,
    groupName,
    hydrated,
    lang,
    onSingleCountrySelected,
    onValuesUnavailable,
  ]);

  return {
    exposedCustomFilterKeys,
    filterData,
    persistedCustomFilterKeys,
    visibleCustomFilters,
  };
}
